// El calendario del widget: lo que en la home hace Litepicker (y, en otros bloques,
// bootstrap-datepicker).
//
// Se queda con los MISMOS nombres de clase que Litepicker (`litepicker`, `month-item`,
// `day-item`, `is-start-date`…), ya prefijados, porque el CSS del buscador tiene 40 reglas
// escritas contra ellos: cambiarlos obligaría a reescribir ese CSS, y lo que se quiere es
// justo lo contrario, que el diseño siga siendo el de hoy.
//
// Las reglas de negocio salen de crearCalendario() y de la configuración de Litepicker:
//   - por defecto ida = mañana y vuelta = pasado mañana; no se puede elegir antes de hoy;
//   - dos meses a la vista, uno solo por debajo de 768px;
//   - los botones de ±1 día tienen cada uno su regla (abajo, en `mover`);
//   - en los bloques con hotel, aceptar con ida == vuelta saca un aviso y resetea las fechas,
//     porque no se puede reservar un hotel de cero noches.
import { formatear, hoy, sumarDias, mismoDia, antesDe } from './fechas.js'
import { aviso } from './modales.js'
import { pintarCalendario, leerClic } from './calendario-render.js'
import { alguienRecorta, sacarFuera } from './portal.js'

/**
 * Los bloques donde el hotel es OBLIGATORIO, y por tanto no admiten entrada y salida el mismo día:
 * sin noche que dormir no hay nada que reservar.
 *
 * `bloquePaqueteTren` NO está aquí a propósito, aunque pueda acabar en hotel. Ahí el hotel se
 * pregunta («¿quieres añadir hotel al viaje?»), así que ida y vuelta en el día es un viaje
 * perfectamente normal —ir y volver— y lo que se hace es no preguntar. Ver `busqueda.js`. La
 * excepción es el buscador de producto `tren-hotel`, que usa ese mismo bloque con el hotel ya
 * elegido: ése vuelve a tenerlo obligatorio, y lo añade `hotelObligatorio()`.
 */
const HOTEL_OBLIGATORIO = ['bloqueTrenHotelActividad', 'bloqueHotel']

/**
 * Con que opcion arranca el viaje: `data-tipo-viaje="solo-ida"` o `"ida-vuelta"`.
 *
 * Sin decir nada manda el markup, que NO es igual en todos los bloques: en Tren + Ocio viene
 * marcado "ida y vuelta" y en Tren + Hotel + Ocio viene marcado "solo ida" --el bug 1 de F0, que
 * se replica a proposito--. Por eso esto no se hace con un booleano: hay que poder pedir las dos
 * cosas, y quien no diga nada se queda exactamente como hoy.
 *
 * Se aceptan las dos formas de escribirlo, con guion y en camello, porque el valor lo escribe una
 * persona en el HTML de una landing. Lo que no se reconozca no cambia nada y avisa: cambiar en
 * silencio con qué arranca un buscador es peor que dejarlo como estaba.
 */
export function tipoViajePedido (pedido) {
  const valor = String(pedido || '').trim().toLowerCase().replace(/[\s_+]/g, '-')
  if (!valor) return null
  if (valor === 'solo-ida' || valor === 'soloida' || valor === 'ida') return 'soloIda'
  if (valor === 'ida-vuelta' || valor === 'idavuelta' || valor === 'ida-y-vuelta') return 'idaVuelta'
  console.warn('[buscador] data-tipo-viaje no reconocido: ' + pedido + ' (se queda el de siempre)')
  return null
}

/**
 * @param {import('./buscador.js').Buscador} buscador
 * @param {string} bloque
 */
export function crearCalendario (buscador, bloque) {
  const contenedor = buscador.nodo(bloque, '.rvb-datepicker-ida-vuelta')
  if (!contenedor) return null

  const estado = buscador.estadoDe(bloque)
  const contenedorSoloIda = buscador.nodo(bloque, '.rvb-datepicker-solo-ida')
  const caja = buscador.nodo(bloque, '.rvb-datepicker-container')

  /** ¿Este buscador lleva hotel sí o sí? Entonces el mismo día no vale. Ver `HOTEL_OBLIGATORIO`. */
  function hotelObligatorio () {
    return HOTEL_OBLIGATORIO.includes(bloque) || buscador.producto === 'tren-hotel'
  }

  // Los cuatro campos: los visibles del bloque de ida y vuelta, y los del bloque de sólo ida.
  const campos = {
    ida: buscador.nodo(bloque, '.rvb-input-daterange .rvb-dateUserFrom'),
    vuelta: buscador.nodo(bloque, '.rvb-input-daterange .rvb-dateUserTo'),
    idaSola: buscador.nodo(bloque, '.rvb-datePicker-inputs.rvb-solo-ida .rvb-dateUserFrom'),
  }

  estado.fechaIda = sumarDias(hoy(), 1)
  estado.fechaVuelta = sumarDias(hoy(), 2)
  // Cuál de las dos fechas se va a fijar con el próximo clic.
  let eligiendo = 'ida'
  let mesVisible = new Date(estado.fechaIda.getFullYear(), estado.fechaIda.getMonth(), 1)

  function pintarCampos () {
    if (campos.ida) campos.ida.value = formatear(estado.fechaIda)
    if (campos.idaSola) campos.idaSola.value = formatear(estado.fechaIda)
    if (campos.vuelta && !estado.soloIda) campos.vuelta.value = formatear(estado.fechaVuelta)
  }

  function pintar () {
    const html = pintarCalendario(mesVisible, {
      ida: estado.fechaIda,
      vuelta: estado.fechaVuelta,
      soloIda: estado.soloIda,
      minimo: hoy(),
      hoy: hoy(),
    })
    contenedor.innerHTML = html
    if (contenedorSoloIda) contenedorSoloIda.innerHTML = html
    pintarCampos()
  }

  /** Un clic en un día: el primero fija la ida, el segundo la vuelta. */
  function elegirDia (tiempo) {
    const dia = new Date(Number(tiempo))
    if (estado.soloIda) {
      estado.fechaIda = dia
    } else if (eligiendo === 'ida') {
      estado.fechaIda = dia
      // Una vuelta anterior a la ida no tiene sentido: se empuja al día siguiente.
      if (!antesDe(estado.fechaIda, estado.fechaVuelta)) estado.fechaVuelta = sumarDias(dia, 1)
      eligiendo = 'vuelta'
    } else {
      if (antesDe(dia, estado.fechaIda)) {
        // Si el segundo clic cae antes que el primero, se toma como nueva ida.
        estado.fechaIda = dia
        estado.fechaVuelta = sumarDias(dia, 1)
        eligiendo = 'vuelta'
      } else {
        estado.fechaVuelta = dia
        eligiendo = 'ida'
      }
    }
    pintar()
  }

  /**
   * Los botones de ±1 día. Cada uno tiene su regla, y son las de la home:
   *   - sumar a la ida arrastra la vuelta sólo si se la comería;
   *   - restar a la ida no puede pasar de hoy;
   *   - restar a la vuelta no puede pasar de la ida;
   *   - sumar a la vuelta no tiene tope.
   */
  function mover (cual, signo) {
    if (cual === 'ida') {
      const nueva = sumarDias(estado.fechaIda, signo)
      if (signo < 0 && antesDe(nueva, hoy())) return
      // La regla de la home, literal: la vuelta sólo se mueve si la ida se le ha pasado por
      // encima (estrictamente). Si la ida queda IGUAL que la vuelta, se quedan iguales — y
      // entonces, en un bloque con hotel, al aceptar salta el aviso de las cero noches. Es
      // el comportamiento de hoy y se replica tal cual.
      if (!estado.soloIda && signo > 0 && antesDe(estado.fechaVuelta, nueva)) {
        estado.fechaVuelta = sumarDias(estado.fechaVuelta, 1)
      }
      estado.fechaIda = nueva
    } else {
      const nueva = sumarDias(estado.fechaVuelta, signo)
      if (signo < 0 && !antesDe(estado.fechaIda, nueva)) return
      estado.fechaVuelta = nueva
    }
    mesVisible = new Date(estado.fechaIda.getFullYear(), estado.fechaIda.getMonth(), 1)
    pintar()
  }

  // --- eventos
  const alPulsar = (e) => {
    const accion = leerClic(e)
    if (!accion) return
    if (accion.mes) {
      mesVisible = new Date(mesVisible.getFullYear(), mesVisible.getMonth() + accion.mes, 1)
      pintar()
      return
    }
    elegirDia(accion.dia.getTime())
  }
  contenedor.addEventListener('click', alPulsar)
  if (contenedorSoloIda) contenedorSoloIda.addEventListener('click', alPulsar)

  // Los ±1 día. Los nombres de clase dicen a qué campo van.
  const botones = [
    ['.rvb-btn-sumar-dia-entrada', 'ida', 1],
    ['.rvb-btn-restar-dia-entrada', 'ida', -1],
    ['.rvb-btn-sumar-dia-salida', 'vuelta', 1],
    ['.rvb-btn-restar-dia-salida', 'vuelta', -1],
    ['.rvb-btn-sumar-dia-solo-entrada', 'ida', 1],
    ['.rvb-btn-restar-dia-solo-entrada', 'ida', -1],
  ]
  botones.forEach(([selector, cual, signo]) => {
    buscador.raiz.querySelectorAll('[data-rvb="' + bloque + '"] ' + selector).forEach((b) => {
      b.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()   // si no, el clic abre el calendario además de mover el día
        if (b.disabled) return
        mover(cual, signo)
      })
    })
  })

  // Abrir el calendario al pulsar en la fila de fechas.
  // Quién enseña y esconde el calendario es la clase `calendarActive` del bloque, igual que en
  // la home: el CSS ya trae `.calendarActive .datepicker-container { display: flex }`. No se
  // toca `rvb-oculto` aquí — lleva !important y ganaría a esa regla.
  // En móvil el calendario se come la pantalla entera, y mientras está abierto la página de
  // debajo no se mueve: la home lo consigue con una clase en el <body>. Es la única regla del
  // widget que sale de su ámbito, y es a propósito — el scroll que hay que parar es el de la
  // página, no el del widget. Se pone y se quita, nunca se queda.
  const ES_MOVIL = () => window.matchMedia('(max-width: 768px)').matches
  const CLASE_BODY = 'rvb-modal-calendar-open'
  // Dónde estaba la página cuando se abrió el calendario, para devolverla al cerrar.
  let scrollAlAbrir = 0

  // Fuera de la portada, el calendario puede caer dentro de algo que lo recorte: en la home de
  // Corporate Benefits el hero lleva `overflow: hidden` y se come media hoja. Cuando pasa, se
  // saca al contenedor de overlays --que cuelga del <body>-- con la cadena de ancestros
  // clonada, que es lo que hace que no pierda un solo estilo. Está explicado en portal.js.
  // Si nadie recorta no se toca nada, y en la portada ni se plantea.
  let devolverCalendario = null
  const ES_PORTADA = () => buscador.raiz.hasAttribute('data-portada')

  const sacarSiLoRecortan = () => {
    if (devolverCalendario || ES_PORTADA() || !caja || !buscador.overlays) return
    if (!alguienRecorta(caja)) return
    devolverCalendario = sacarFuera(caja, buscador.raiz, buscador.overlays, { colocar: !ES_MOVIL() })
  }

  // Al cambiar el tamaño de la ventana el hueco de la landing se mueve: se devuelve y se vuelve
  // a sacar, que es más corto que recalcular y no se ve.
  const alRedimensionar = () => {
    if (!devolverCalendario) return
    devolverCalendario()
    devolverCalendario = null
    sacarSiLoRecortan()
  }

  const abrir = () => {
    const raizBloque = buscador.raiz.querySelector('[data-rvb="' + bloque + '"]')
    if (raizBloque) raizBloque.classList.add('rvb-calendarActive')
    if (ES_MOVIL()) {
      // SE SUBE AL PRINCIPIO ANTES DE BLOQUEAR, Y ESE ORDEN ES EL ARREGLO.
      //
      // La clase del body deja el documento en `height: 100%` con `overflow-y: hidden`. Si la
      // página estaba desplazada, iOS lo recorta y REAJUSTA EL SCROLL de golpe, y durante ese
      // reajuste el viewport visual queda descolocado y EL PRIMER TOQUE SE PIERDE: el navegador
      // lo da por consumido antes de convertirlo en clic.
      //
      // Medido en el iPhone de un cliente el 18/09/2026, con la página en 439px: al abrir el
      // calendario el scroll saltó a 234 --exactamente los 205px que el viewport visual quedó
      // desplazado hacia arriba-- y el toque sobre el día 20 no dio clic. Los seis siguientes,
      // ya con el viewport en su sitio, funcionaron todos. De ahí que él lo contara como que
      // "si pongo la ciudad de origen antes, el día no responde": rellenar la estación es lo
      // que deja la página desplazada.
      //
      // Subiendo antes, el reajuste ocurre al abrir --cuando nadie está tocando-- y el
      // calendario recibe los toques con todo quieto. Es lo que hace la home, y lo que ya hacía
      // el panel de estaciones (ver panel-movil.js); al calendario se le había quedado sin
      // poner. No se reproduce ni en Chrome con un iPhone emulado ni en WebKit: hace falta el
      // iOS de verdad, con su barra de navegación.
      scrollAlAbrir = window.pageYOffset || document.documentElement.scrollTop || 0
      window.scrollTo(0, 0)
      document.body.classList.add(CLASE_BODY)
    }
    pintar()
    // Después de pintar: hasta que no está a la vista no tiene medidas que copiar.
    sacarSiLoRecortan()
    window.addEventListener('resize', alRedimensionar)
  }
  buscador.raiz.querySelectorAll('[data-rvb="' + bloque + '"] .rvb-datePicker-inputs').forEach((fila) => {
    fila.addEventListener('click', abrir)
  })

  // Aceptar y borrar.
  const cerrar = () => {
    window.removeEventListener('resize', alRedimensionar)
    if (devolverCalendario) { devolverCalendario(); devolverCalendario = null }
    const raizBloque = buscador.raiz.querySelector('[data-rvb="' + bloque + '"]')
    if (raizBloque) raizBloque.classList.remove('rvb-calendarActive')
    document.body.classList.remove(CLASE_BODY)
    // Y la página vuelve donde estaba: subirla es el precio de poder bloquearla sin saltos,
    // pero dejar al cliente en la cabecera después de elegir las fechas no lo es.
    if (scrollAlAbrir) { window.scrollTo(0, scrollAlAbrir); scrollAlAbrir = 0 }
  }
  buscador.raiz.querySelectorAll('[data-rvb="' + bloque + '"] .rvb-btn-aceptar-fechas').forEach((b) => {
    b.addEventListener('click', (e) => {
      e.preventDefault()
      // Donde el hotel es obligatorio, entrada y salida el mismo día no vale: cero noches.
      if (hotelObligatorio() && !estado.soloIda && mismoDia(estado.fechaIda, estado.fechaVuelta)) {
        aviso(buscador, {
          titulo: buscador.texto('texto-aviso-titulo-swal'),
          texto: buscador.texto('texto-aviso-texto-swal'),
          confirmar: buscador.texto('texto-aviso-confirmar-swal'),
        }).then(() => {
          estado.fechaIda = sumarDias(hoy(), 1)
          estado.fechaVuelta = sumarDias(hoy(), 2)
          pintar()
          cerrar()
        })
        return
      }
      cerrar()

      // IDA Y VUELTA EL MISMO DÍA, y sólo en ese caso: aceptar las fechas sigue el flujo en vez
      // de dejarlo muerto. Como el hotel ya no se pregunta —no hay noche que dormir—, aceptar se
      // quedaba sin nada detrás; ahora abre el selector de viajeros, igual que haría el botón de
      // buscar, y de ahí se va a la búsqueda. Cualquier otra combinación de fechas se queda como
      // estaba: aceptar cierra el calendario y ya.
      //
      // La regla de «mismo día» NO se repite aquí: la decide `busqueda.js`, que es de quien es.
      // Y se lee en el momento del clic porque el calendario se crea ANTES que la búsqueda.
      const busqueda = buscador.busquedaDe && buscador.busquedaDe[bloque]
      if (busqueda && busqueda.idaYVueltaElMismoDia && busqueda.idaYVueltaElMismoDia()) {
        busqueda.continuarSinHotel()
      }
    })
  })
  buscador.raiz.querySelectorAll('[data-rvb="' + bloque + '"] .rvb-btn-borrar-fechas').forEach((b) => {
    b.addEventListener('click', (e) => {
      e.preventDefault()
      estado.fechaIda = sumarDias(hoy(), 1)
      estado.fechaVuelta = sumarDias(hoy(), 2)
      pintar()
    })
  })

  // Los radios de sólo ida / ida y vuelta.
  buscador.raiz.querySelectorAll('[data-rvb="' + bloque + '"] input[type="radio"]').forEach((radio) => {
    radio.addEventListener('change', () => {
      estado.soloIda = radio.value === 'soloIda' && radio.checked
      const idaVuelta = buscador.nodo(bloque, '.rvb-datePicker-inputs.rvb-input-daterange')
      const soloIda = buscador.nodo(bloque, '.rvb-datePicker-inputs.rvb-solo-ida')
      // OJO: el bloque "Tren + Hotel + Ocio" no tiene fila de sólo ida en el markup, y ése es
      // justo el bug 1 de F0, que se replica a propósito: allí el radio "Sólo ida" viene
      // marcado de serie y el buscador se queda sin fila de fechas que leer.
      if (idaVuelta) idaVuelta.classList.toggle('rvb-oculto', estado.soloIda)
      if (soloIda) soloIda.classList.toggle('rvb-oculto', !estado.soloIda)
      const contSolo = buscador.nodo(bloque, '.rvb-datepicker-solo-ida')
      const contIda = buscador.nodo(bloque, '.rvb-datepicker-ida-vuelta')
      if (contSolo) contSolo.classList.toggle('rvb-oculto', !estado.soloIda)
      if (contIda) contIda.classList.toggle('rvb-oculto', estado.soloIda)
      pintar()
    })
    // El estado inicial sale del markup, que es donde está marcado el valor por defecto.
    if (radio.checked && radio.value === 'soloIda') estado.soloIda = true
  })

  // Y si la landing pide uno, se marca ese. Se dispara el `change` en vez de tocar el estado a
  // mano para que pase por el mismo camino que un clic del cliente: el estado, las dos filas de
  // fechas que se enseñan o se esconden, y el repintado. Los radios comparten `name` --montar.js
  // lo pone con el prefijo de la instancia-- así que marcar uno desmarca el otro él solo.
  const pedido = tipoViajePedido((buscador.opciones || {}).tipoViaje)
  if (pedido) {
    const radios = [...buscador.raiz.querySelectorAll('[data-rvb="' + bloque + '"] input[type="radio"]')]
    const queria = radios.find((r) => r.value === pedido)
    if (queria && !queria.checked) {
      queria.checked = true
      queria.dispatchEvent(new Event('change'))
    }
  }

  pintar()

  return {
    pintar,
    destruir () {
      window.removeEventListener('resize', alRedimensionar)
      if (devolverCalendario) { devolverCalendario(); devolverCalendario = null }
      contenedor.removeEventListener('click', alPulsar)
      if (contenedorSoloIda) contenedorSoloIda.removeEventListener('click', alPulsar)
    },
  }
}
